import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';

const PushNotificationContext = createContext(null);

const STORAGE_KEY = 'gs.pushInbox.v1';
const MAX_HISTORY = 50;

const loadFromStorage = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { items: [], unread: 0 };
    const data = JSON.parse(raw);
    return { items: data.items || [], unread: data.unread || 0 };
  } catch (_) {
    return { items: [], unread: 0 };
  }
};

const saveToStorage = (state) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (_) {
    /* ignore quota errors */
  }
};

/**
 * PushNotificationProvider — listens for push.received messages from the SW
 * and maintains a small history + unread counter. Wraps the whole app so any
 * component can read state via usePushInbox().
 */
export function PushNotificationProvider({ children }) {
  const [items, setItems] = useState(() => loadFromStorage().items);
  const [unread, setUnread] = useState(() => loadFromStorage().unread);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const handler = (event) => {
      const data = event.data;
      if (!data || data.type !== 'push.received') return;
      const item = {
        id: `${data.at || Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        title: data.title,
        body: data.body,
        url: data.url,
        extra: data.extra || {},
        at: data.at || Date.now(),
      };
      setItems((prev) => {
        const next = [item, ...prev].slice(0, MAX_HISTORY);
        return next;
      });
      setUnread((prev) => prev + 1);
      // In-app toast, styled per event type. Visible only when the app tab is
      // focused; the OS-level notification is fired independently by the SW.
      const eventType = item.extra?.event;
      const toastOpts = {
        description: item.body,
        duration: 6000,
        action: item.url ? {
          label: 'Voir',
          onClick: () => {
            // Prefer client-side nav when possible so we don't reload the app.
            if (item.url.startsWith('/')) {
              window.history.pushState({}, '', item.url);
              window.dispatchEvent(new PopStateEvent('popstate'));
            } else {
              window.location.href = item.url;
            }
          },
        } : undefined,
      };
      if (eventType === 'absence_justified' || eventType === 'child_linked') {
        toast.success(item.title, toastOpts);
      } else if (eventType === 'student_absent' || eventType === 'absence_reminder') {
        toast.warning(item.title, toastOpts);
      } else {
        toast.message(item.title, toastOpts);
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, []);

  // Persist to localStorage on every change
  useEffect(() => {
    saveToStorage({ items, unread });
  }, [items, unread]);

  const markAllRead = useCallback(() => setUnread(0), []);
  const clear = useCallback(() => {
    setItems([]);
    setUnread(0);
  }, []);

  return (
    <PushNotificationContext.Provider value={{ items, unread, markAllRead, clear }}>
      {children}
    </PushNotificationContext.Provider>
  );
}

export function usePushInbox() {
  const ctx = useContext(PushNotificationContext);
  if (!ctx) {
    // Not wrapped — return safe defaults so components don't crash.
    return { items: [], unread: 0, markAllRead: () => {}, clear: () => {} };
  }
  return ctx;
}
